import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AppLogo from '../components/AppLogo';
import { ArrowRight, Mail, Phone, CheckCircle2, HeartHandshake, Compass, Building2, HandHeart, Church, Sparkles, GraduationCap, Stethoscope, BookOpen, PlayCircle } from 'lucide-react';

const SHANNON_PORTRAIT = '/images/shannon-portrait.jpg';
const FACILITATION_GROUP = '/images/facilitation-group.jpg';

const GALLERY = [
  { src: '/images/workshop-reading.jpg', alt: 'Shannon Ivey reading aloud from her book while facilitating a session' },
  { src: '/images/workshop-exercise.jpg', alt: 'Workshop participants spread across the room mid-exercise while a facilitator gives direction' },
  { src: '/images/workshop-energy.jpg', alt: 'Care professionals standing in a circle with arms raised during an experiential exercise' },
  { src: '/images/workshop-pair.jpg', alt: 'Two workshop participants working together in a paired practice exercise' },
  { src: '/images/workshop-gallery-wall.jpg', alt: 'Shannon Ivey in front of a wall of participant portraits from a community workshop' },
];

const AREAS = [
  { value: 'training', label: 'The Art of Caring — Training' },
  { value: 'coaching', label: 'Care Team Lab — Coaching & Technical Assistance' },
  { value: 'technology', label: 'CircleUpSquad — Technology' },
  { value: 'multiple', label: 'Multiple Services' },
  { value: 'general', label: 'General Inquiry' },
];

const AUDIENCES = [
  { icon: Stethoscope, label: 'Healthcare organizations and cancer support programs' },
  { icon: HeartHandshake, label: 'Community health worker programs' },
  { icon: Compass, label: 'Patient advocacy and navigation organizations' },
  { icon: Building2, label: 'Nonprofits and community-based organizations' },
  { icon: Church, label: 'Faith communities' },
  { icon: HandHeart, label: 'Peer support and volunteer programs' },
  { icon: GraduationCap, label: 'Educational and professional development organizations' },
];

const Bullets = ({ items, testid }) => (
  <ul className="space-y-2" data-testid={testid}>
    {items.map((t) => (
      <li key={t} className="flex items-start gap-2.5 text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed">
        <CheckCircle2 className="w-[18px] h-[18px] text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Offering = ({ num, title, subtitle, lede, intro, blocks, cta, onCta, testid, accent }) => (
  <section className="scroll-mt-20" data-testid={testid} aria-labelledby={`${testid}-heading`}>
    <div className={`rounded-3xl border ${accent.border} ${accent.bg} p-6 sm:p-10`}>
      <div className="flex items-baseline gap-3 mb-5">
        <span className={`text-4xl sm:text-5xl font-bold tabular-nums ${accent.num}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{num}</span>
        <span className={`h-px flex-1 ${accent.rule}`} aria-hidden="true" />
      </div>
      <h3 id={`${testid}-heading`} className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {title}
      </h3>
      <p className={`text-base md:text-lg font-semibold mt-1.5 ${accent.num}`}>{subtitle}</p>
      <p className="text-lg sm:text-xl text-foreground/90 font-medium mt-5 italic" style={{ fontFamily: 'Outfit, sans-serif' }}>{lede}</p>
      {intro.map((p) => (
        <p key={p.slice(0, 30)} className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed mt-4">{p}</p>
      ))}
      <div className="grid sm:grid-cols-2 gap-x-10 gap-y-7 mt-8">
        {blocks.map((b) => (
          <div key={b.heading}>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-3">{b.heading}</p>
            <Bullets items={b.items} />
          </div>
        ))}
      </div>
      {cta.note && <p className="text-xs text-muted-foreground mt-7 leading-relaxed">{cta.note}</p>}
      <Button
        onClick={onCta}
        className={`mt-7 h-12 px-6 rounded-full text-[15px] font-semibold ${accent.btn}`}
        data-testid={`${testid}-cta`}
      >
        {cta.label} <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
      </Button>
    </div>
  </section>
);

export default function PartnerPage() {
  const formRef = useRef(null);
  const servicesRef = useRef(null);
  const areaRef = useRef(null);

  const [form, setForm] = useState({ name: '', org: '', email: '', area: 'general', note: '', website: '' });
  const [human, setHuman] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'For Organizations | CircleUpSquad';
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', "Explore CircleUpSquad's workforce training, coaching and technical assistance, and care coordination technology. Care as a Collective Practice.");
    return () => {
      document.title = prevTitle;
      if (prevDesc) meta.setAttribute('content', prevDesc);
    };
  }, []);

  const scrollToForm = useCallback((area) => {
    if (area) setForm((f) => ({ ...f, area }));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => areaRef.current?.focus({ preventScroll: true }), 600);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.org.trim() || !form.email.trim() || !form.note.trim()) {
      toast.error('Please fill in your name, organization, email and message.');
      return;
    }
    if (!human) {
      toast.error('Please confirm you\'re a person before sending.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/partner-inquiry`, {
        name: form.name.trim(),
        org: form.org.trim(),
        email: form.email.trim(),
        area: form.area,
        note: form.note.trim(),
        website: form.website,
        human: true,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send your inquiry. Please email shannon@shannon-ivey.com directly.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-background" data-testid="partner-page">
      {/* Top bar — For Individuals | Logo | For Organizations */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-stone-50/85 dark:bg-background/85 border-b border-stone-200/70 dark:border-border/40">
        <nav className="max-w-5xl mx-auto px-3 sm:px-5 h-14 grid grid-cols-[1fr_auto_1fr] items-center gap-2" aria-label="Primary">
          <Link
            to="/individuals"
            className="justify-self-start text-[12px] sm:text-sm font-medium text-muted-foreground hover:text-foreground px-1.5 py-1 rounded-md transition-colors"
            data-testid="nav-for-individuals"
          >
            For Individuals
          </Link>
          <Link to="/" className="justify-self-center flex items-center gap-2" data-testid="partner-home-link">
            <AppLogo />
            <span className="hidden sm:inline text-sm font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>CircleUpSquad</span>
          </Link>
          <span
            className="justify-self-end text-[12px] sm:text-sm font-semibold text-cyan-700 dark:text-cyan-400 px-1.5 py-1"
            aria-current="page"
            data-testid="nav-for-organizations-current"
          >
            For Organizations
          </span>
        </nav>
      </header>

      {/* HERO */}
      <section className="px-5 pt-12 pb-14 sm:pt-20 sm:pb-20" data-testid="partner-hero">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-400">CircleUpSquad</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-[1.05] mt-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="partner-hero-title"
            >
              Care as a<br />Collective Practice
            </h1>
            <p className="text-base md:text-lg font-semibold text-foreground/90 mt-6 leading-snug">
              Better care takes more than good intentions.<br />
              We teach the skills. We support the people. We provide the tools.
            </p>
            <div className="mt-6 space-y-4 max-w-2xl">
              <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed">
                CircleUpSquad helps organizations strengthen how care is communicated, coordinated, and delivered.
              </p>
              <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed">
                Through experiential workforce training, coaching and technical assistance, and practical care coordination technology, we help organizations and communities build stronger networks of support.
              </p>
              <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed">
                Our services can be offered independently or combined into customized organizational partnerships.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <Button
                onClick={() => servicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="h-[52px] px-7 rounded-full text-[15px] font-semibold bg-gradient-to-r from-cyan-700 to-emerald-600 hover:from-cyan-800 hover:to-emerald-700 text-white shadow-lg shadow-cyan-900/15"
                data-testid="partner-hero-services-btn"
              >
                Explore Our Services <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                onClick={() => scrollToForm()}
                className="h-[52px] px-7 rounded-full text-[15px] font-semibold border-stone-300 dark:border-border"
                data-testid="partner-hero-contact-btn"
              >
                Start a Conversation
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-cyan-200/50 to-emerald-200/40 dark:from-cyan-900/30 dark:to-emerald-900/20" aria-hidden="true" />
            <img
              src={FACILITATION_GROUP}
              alt="Care professionals standing in a workshop circle with hands raised during a CircleUpSquad experiential training exercise"
              className="relative w-full aspect-[4/5] object-cover rounded-[1.75rem] shadow-xl"
              data-testid="partner-hero-image"
            />
          </div>
        </div>
      </section>

      {/* RING THEORY */}
      <section className="px-5 py-14 sm:py-20 bg-stone-900 dark:bg-slate-950 text-stone-100" data-testid="partner-ring-theory">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-400">The framework underneath the work</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Comfort in. Dump out.
            </h2>
            <div className="mt-5 space-y-4 max-w-2xl">
              <p className="text-[15px] text-stone-300 leading-relaxed">
                Our training, coaching, and technology all share one foundation: <strong className="text-white font-semibold">Ring Theory</strong>. Picture concentric circles. The person living the hardest thing sits at the center. Their closest people form the next ring, then friends, colleagues, and community beyond that.
              </p>
              <p className="text-[15px] text-stone-300 leading-relaxed">
                Support flows <em>inward</em>, toward the center. Worry, frustration, and grief flow <em>outward</em>, to someone further from it. Nobody vents toward the person with less capacity than them.
              </p>
              <p className="text-[15px] text-stone-300 leading-relaxed">
                It sounds simple. In practice it changes how a care team talks, how a volunteer offers help, and how a patient gets support that actually lands — which is exactly what we teach, coach, and build toward.
              </p>
            </div>
          </div>
          <div className="justify-self-center" aria-hidden="true">
            <div className="relative w-[230px] h-[230px] sm:w-[280px] sm:h-[280px]">
              {[
                { size: '100%', label: 'Community', color: 'border-stone-700' },
                { size: '70%', label: 'Close circle', color: 'border-cyan-800' },
                { size: '40%', label: 'Center', color: 'border-cyan-500' },
              ].map((ring) => (
                <div
                  key={ring.label}
                  className={`absolute rounded-full border-2 ${ring.color} flex items-start justify-center pt-2`}
                  style={{ width: ring.size, height: ring.size, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                >
                  <span className="text-[10px] uppercase tracking-[0.14em] text-stone-400 bg-stone-900 dark:bg-slate-950 px-2 -mt-[9px]">{ring.label}</span>
                </div>
              ))}
              <HeartHandshake className="absolute w-7 h-7 text-cyan-400" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* THREE WAYS */}
      <section ref={servicesRef} id="services" className="px-5 py-14 sm:py-20 bg-white dark:bg-slate-900/40 border-y border-stone-200/70 dark:border-border/40 scroll-mt-14" data-testid="partner-services">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Three Ways to Strengthen Care
          </h2>
          <div className="mt-10 space-y-8">
            <Offering
              testid="offering-training"
              num="01"
              title="The Art of Caring"
              subtitle="Workforce Training & Professional Development"
              lede="Care is something we practice."
              intro={['The Art of Caring offers experiential learning designed to strengthen the communication, collaboration, and practical skills of people working in care-centered roles.']}
              blocks={[
                { heading: 'Designed for', items: ['Community health workers (CHWs)', 'Peer navigators', 'Volunteer mentors', 'Victim advocates', 'Patient support professionals', 'Community-based care teams'] },
                { heading: 'Training topics', items: ['Coaching-informed communication and motivational interviewing', 'Collaborative action planning, autonomy, and advocacy', 'Professional boundaries, referrals, and support-network coordination'] },
                { heading: 'Available formats', items: ['90-minute experiential workshop', '3-hour intensive', 'Customized modular workforce training'] },
              ]}
              cta={{ label: 'Explore Training Opportunities', note: 'Continuing education curricula are in development. CEU approval has not yet been established.' }}
              onCta={() => scrollToForm('training')}
              accent={{ border: 'border-cyan-200/80 dark:border-cyan-900/50', bg: 'bg-cyan-50/60 dark:bg-cyan-950/20', num: 'text-cyan-700 dark:text-cyan-400', rule: 'bg-cyan-300/60', btn: 'bg-cyan-700 hover:bg-cyan-800 text-white' }}
            />
            <Offering
              testid="offering-coaching"
              num="02"
              title="Care Team Lab"
              subtitle="Coaching & Technical Assistance"
              lede="Supporting the people who support others."
              intro={[
                'Strong care programs require thoughtful design, effective communication, and opportunities for teams to reflect, adapt, and grow.',
                'Care Team Lab provides customized coaching and technical assistance for organizations developing or strengthening patient navigation, peer support, volunteer engagement, and community-based care programs.',
              ]}
              blocks={[
                { heading: 'Services include', items: ['Program development, implementation, and workflow design', 'Coaching for navigators, peer supporters, and team leaders', 'Reflective practice, training reinforcement, and evaluation', 'Communication and support-network engagement strategies'] },
                { heading: 'Our approach', items: ['Professional coaching', 'Practical learning design', 'Collaborative problem-solving', 'Tailored to organizational needs'] },
              ]}
              cta={{ label: 'Explore Care Team Lab' }}
              onCta={() => scrollToForm('coaching')}
              accent={{ border: 'border-amber-200/80 dark:border-amber-900/50', bg: 'bg-amber-50/70 dark:bg-amber-950/20', num: 'text-amber-700 dark:text-amber-400', rule: 'bg-amber-300/60', btn: 'bg-amber-700 hover:bg-amber-800 text-white' }}
            />
            <Offering
              testid="offering-technology"
              num="03"
              title="CircleUpSquad"
              subtitle="Care Coordination Technology"
              lede="Make it easier for people to show up."
              intro={[
                'When someone experiences illness, friends, family, and community members often want to help. But organizing that support can become another responsibility for the person who needs care.',
                'CircleUpSquad is a digital platform designed to make practical support easier to communicate and coordinate.',
              ]}
              blocks={[
                { heading: 'The platform helps people', items: ['Communicate needs', 'Organize offers of help', 'Activate existing support networks', 'Coordinate practical assistance'] },
                { heading: 'For organizations', items: ['Organizational pilots', 'Platform onboarding', 'Multi-site partnerships'] },
              ]}
              cta={{ label: 'Explore Technology Partnerships' }}
              onCta={() => scrollToForm('technology')}
              accent={{ border: 'border-emerald-200/80 dark:border-emerald-900/50', bg: 'bg-emerald-50/60 dark:bg-emerald-950/20', num: 'text-emerald-700 dark:text-emerald-400', rule: 'bg-emerald-300/60', btn: 'bg-emerald-700 hover:bg-emerald-800 text-white' }}
            />
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="px-5 pt-14 sm:pt-20 pb-10" data-testid="partner-gallery">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-400">Inside the Room</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What this looks like in practice
          </h2>
          <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed mt-4 max-w-2xl">
            Real sessions with real care teams — moving, practising, and figuring it out together.
          </p>
        </div>
        <div className="max-w-5xl mx-auto mt-8">
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3" data-testid="partner-gallery-strip">
          {GALLERY.map((img, i) => (
            <button
              key={img.src}
              type="button"
              onClick={() => setLightbox(img)}
              className="snap-start flex-shrink-0 w-[220px] sm:w-[250px] rounded-2xl overflow-hidden shadow-md transition-transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid={`partner-gallery-item-${i}`}
            >
              <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-[300px] sm:h-[330px] object-cover" />
            </button>
          ))}
          </div>
        </div>
      </section>

      <section className="px-5 pt-4 pb-14 sm:pb-20" data-testid="partner-audiences">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-400">Who We Work With</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Different communities.<br className="hidden sm:block" /> Shared responsibility.
          </h2>
          <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed mt-5 max-w-2xl">
            Our work is designed for organizations and professionals supporting people through illness, caregiving, and experiences that require coordinated community support.
          </p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-9">
            {AUDIENCES.map(({ icon: Icon, label }) => (
              <li key={label} className="rounded-2xl bg-white dark:bg-slate-900/40 border border-stone-200/80 dark:border-border/40 p-5 flex items-start gap-3 transition-transform hover:-translate-y-0.5">
                <span className="w-9 h-9 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-[18px] h-[18px] text-cyan-700 dark:text-cyan-400" aria-hidden="true" />
                </span>
                <span className="text-[14px] font-medium text-foreground leading-snug">{label}</span>
              </li>
            ))}
          </ul>
          <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed mt-8 max-w-2xl">
            Whether an organization is interested in a single workshop or a longer-term partnership, we can explore an approach that fits its community.
          </p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="px-5 py-14 sm:py-20 bg-white dark:bg-slate-900/40 border-y border-stone-200/70 dark:border-border/40" data-testid="partner-founder">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[auto_1fr] gap-9 lg:gap-14 items-start">
          <div className="flex flex-col items-center lg:items-start gap-4">
            <img
              src={SHANNON_PORTRAIT}
              alt="Portrait of Shannon Ivey, MFA, PCC"
              className="w-36 h-36 sm:w-56 sm:h-56 rounded-full object-cover bg-white ring-4 ring-white shadow-lg"
              data-testid="partner-founder-image"
            />
            <div className="flex flex-col gap-2 w-full sm:w-56">
              <a
                href="https://shannon-ivey.com/bookspeaking"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border border-stone-300 dark:border-border bg-white dark:bg-slate-900/60 text-sm font-semibold text-foreground hover:border-cyan-600 hover:text-cyan-700 dark:hover:text-cyan-400 transition-colors"
                data-testid="founder-book-link"
              >
                <BookOpen className="w-4 h-4" aria-hidden="true" /> Read the book
              </a>
              <a
                href="https://www.youtube.com/watch?v=SloEhSgZOQ4"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border border-stone-300 dark:border-border bg-white dark:bg-slate-900/60 text-sm font-semibold text-foreground hover:border-cyan-600 hover:text-cyan-700 dark:hover:text-cyan-400 transition-colors"
                data-testid="founder-tedx-link"
              >
                <PlayCircle className="w-4 h-4" aria-hidden="true" /> Watch the TEDx talk
              </a>
              <a
                href="mailto:shannon@shannon-ivey.com?subject=Hello%20from%20CircleUpSquad.com"
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border border-stone-300 dark:border-border bg-white dark:bg-slate-900/60 text-sm font-semibold text-foreground hover:border-cyan-600 hover:text-cyan-700 dark:hover:text-cyan-400 transition-colors"
                data-testid="founder-email-link"
              >
                <Mail className="w-4 h-4" aria-hidden="true" /> Email Shannon
              </a>
            </div>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-400">Meet the Founder</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Shannon Ivey, MFA, PCC
            </h2>
            <p className="text-base md:text-lg font-semibold text-foreground/90 mt-1.5">Founder &amp; CEO, CircleUpSquad</p>
            <p className="text-sm text-muted-foreground mt-2">Healthcare educator. Professional certified coach. Author. Patient advocate.</p>
            <div className="mt-6 space-y-4 max-w-2xl">
              <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed">
                Shannon brings together professional experience in education, coaching, facilitation, and patient advocacy with lived experience navigating serious illness.
              </p>
              <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed">
                Her work explores how people communicate, collaborate, advocate for themselves, and build meaningful support systems.
              </p>
              <blockquote className="border-l-2 border-cyan-500 pl-5 text-lg sm:text-xl text-foreground/90 font-medium leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CircleUpSquad grew from a belief that care should not depend on one person’s ability to organize everyone around them. Care is a collective practice. We can get better at it together.
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY */}
      <section ref={formRef} id="contact" className="px-5 py-14 sm:py-20 scroll-mt-14" data-testid="partner-inquiry">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-400">Organizational Inquiry</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Let’s Build Better Care Together
          </h2>
          <p className="text-[15px] text-stone-700 dark:text-stone-300 leading-relaxed mt-5">
            Interested in workforce training, coaching and technical assistance, or care coordination technology? Tell us a little about your organization and what you’re hoping to strengthen. We welcome conversations about how CircleUpSquad might support your work.
          </p>

          {submitted ? (
            <div className="mt-8 rounded-3xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/70 dark:bg-emerald-950/20 p-8 text-center" data-testid="partner-form-success">
              <span className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-6 h-6 text-white" aria-hidden="true" />
              </span>
              <p className="text-lg font-semibold text-foreground mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Thank you for reaching out! We’ve received your inquiry and look forward to learning more about your organization.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Shannon will follow up at {form.email}. Need something sooner? Call 252-599-1099.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 rounded-3xl bg-white dark:bg-slate-900/40 border border-stone-200/80 dark:border-border/40 p-6 sm:p-8 space-y-5" noValidate data-testid="partner-form">
              {/* honeypot — hidden from humans and screen readers */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="partner-website">Website</label>
                <input id="partner-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label htmlFor="partner-name" className="text-sm font-semibold text-foreground">Name <span className="text-red-600" aria-hidden="true">*</span></label>
                  <Input id="partner-name" required value={form.name} onChange={set('name')} autoComplete="name" className="rounded-xl h-11" data-testid="partner-name-input" />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="partner-org" className="text-sm font-semibold text-foreground">Organization <span className="text-red-600" aria-hidden="true">*</span></label>
                  <Input id="partner-org" required value={form.org} onChange={set('org')} autoComplete="organization" className="rounded-xl h-11" data-testid="partner-org-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="partner-email" className="text-sm font-semibold text-foreground">Email <span className="text-red-600" aria-hidden="true">*</span></label>
                <Input id="partner-email" type="email" required value={form.email} onChange={set('email')} autoComplete="email" className="rounded-xl h-11" data-testid="partner-email-input" />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="partner-area" className="text-sm font-semibold text-foreground">Area of Interest</label>
                <select
                  id="partner-area"
                  ref={areaRef}
                  value={form.area}
                  onChange={set('area')}
                  className="w-full h-11 rounded-xl border border-input bg-background px-3 text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  data-testid="partner-area-select"
                >
                  {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="partner-message" className="text-sm font-semibold text-foreground">Message <span className="text-red-600" aria-hidden="true">*</span></label>
                <Textarea id="partner-message" required rows={5} value={form.note} onChange={set('note')} className="rounded-xl" placeholder="What are you hoping to strengthen?" data-testid="partner-message-input" />
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="partner-human" checked={human} onCheckedChange={(v) => setHuman(v === true)} className="mt-0.5" data-testid="partner-human-checkbox" />
                <label htmlFor="partner-human" className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed cursor-pointer">
                  I’m a person, not a bot.
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto h-12 px-8 rounded-full text-[15px] font-semibold bg-gradient-to-r from-cyan-700 to-emerald-600 hover:from-cyan-800 hover:to-emerald-700 text-white"
                data-testid="partner-submit-btn"
              >
                {submitting ? 'Sending…' : 'Start a Conversation'}
              </Button>
            </form>
          )}

          <div className="mt-10 rounded-3xl border border-stone-200/80 dark:border-border/40 p-6 sm:p-8" data-testid="partner-direct-contact">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Direct Contact</p>
            <p className="text-lg font-bold text-foreground mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Shannon Ivey, MFA, PCC</p>
            <p className="text-sm text-muted-foreground">Founder &amp; CEO, CircleUpSquad</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-7 mt-4">
              <a href="mailto:shannon@shannon-ivey.com" className="inline-flex items-center gap-2 text-[15px] font-medium text-cyan-700 dark:text-cyan-400 hover:underline" data-testid="partner-contact-email">
                <Mail className="w-4 h-4" aria-hidden="true" /> shannon@shannon-ivey.com
              </a>
              <a href="tel:+12525991099" className="inline-flex items-center gap-2 text-[15px] font-medium text-cyan-700 dark:text-cyan-400 hover:underline" data-testid="partner-contact-phone">
                <Phone className="w-4 h-4" aria-hidden="true" /> 252-599-1099
              </a>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-2xl p-3 sm:p-4" data-testid="partner-gallery-lightbox">
          <DialogHeader className="sr-only"><DialogTitle>Workshop photo</DialogTitle></DialogHeader>
          {lightbox && (
            <img src={lightbox.src} alt={lightbox.alt} className="w-full max-h-[75vh] object-contain rounded-xl" />
          )}
        </DialogContent>
      </Dialog>

      <footer className="px-5 py-10 border-t border-stone-200/70 dark:border-border/40">        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-cyan-600" aria-hidden="true" /> Care is a collective practice.
          </p>
          <Link to="/individuals" className="text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline" data-testid="partner-footer-home-link">
            CircleUpSquad for individuals and families
          </Link>
        </div>
      </footer>
    </div>
  );
}
